import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Search } from "lucide-react";
import { api } from "@/lib/api";
import type { Creator } from "@/lib/types";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

export function DiscoverPage() {
  const [query, setQuery] = useState("");
  const [creators, setCreators] = useState<Creator[] | null>(null);

  useEffect(() => {
    const h = setTimeout(async () => {
      setCreators(null);
      const res = await api.get<{ creators: Creator[] }>(
        `/api/creators?q=${encodeURIComponent(query)}`,
      );
      setCreators(res.creators);
    }, 180);
    return () => clearTimeout(h);
  }, [query]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-3xl font-semibold tracking-tight">Discover creators</h1>
        <p className="text-sm text-muted-foreground">
          Browse who's open, what they're offering, and what the current turnaround looks like.
        </p>
      </div>
      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search handles, names, or bios"
          value={query}
          onChange={e => setQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {!creators ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-36 w-full rounded-lg" />
          ))}
        </div>
      ) : creators.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No creators match.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {creators.map(c => (
            <Link
              key={c.id}
              to={`/${c.handle}`}
              className="group rounded-lg border bg-card p-4 transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="flex items-start gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={c.avatar_url ?? undefined} />
                  <AvatarFallback>
                    {(c.display_name ?? c.handle).slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate font-medium">{c.display_name ?? `@${c.handle}`}</p>
                    <Badge variant={c.creator_open ? "default" : "secondary"}>
                      {c.creator_open ? "Open" : "Waitlist"}
                    </Badge>
                  </div>
                  <p className="truncate text-xs text-muted-foreground">@{c.handle}</p>
                </div>
              </div>
              {c.bio && (
                <p className="mt-3 line-clamp-3 text-sm text-muted-foreground">{c.bio}</p>
              )}
              {c.creator_turnaround_days && (
                <p className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" /> typical turnaround ~{c.creator_turnaround_days}d
                </p>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
